/**
 * Tabs — an accessible tab list for switching sibling content panels.
 *
 * WHY A COMPONENT AND NOT INLINE JSX
 * ----------------------------------
 * The pattern (a row of pill-shaped tabs above a content area) appears in
 * more than one place as the site grows — the 「可預約主題」 section on
 * /lectures is the first. Writing the ARIA wiring once means the second
 * place gets keyboard support for free instead of re-deriving it.
 *
 * ACCESSIBILITY CONTRACT
 * ----------------------
 * This follows the WAI-ARIA Tabs pattern:
 *   - the container is `role="tablist"`, each button `role="tab"`, each
 *     panel `role="tabpanel"`
 *   - the active tab carries `aria-selected="true"`; inactive tabs carry
 *     `aria-selected="false"` and `tabIndex={-1}` so Tab moves past the tab
 *     strip to the panel rather than through every tab
 *   - each tab has `aria-controls` pointing at its panel's id, and each
 *     panel has `aria-labelledby` pointing back
 *   - Left/Right (and Home/End) move between tabs, per the ARIA authoring
 *     practices. This is a Roving Tabindex: only the active tab is in the
 *     Tab order, and the arrow keys move focus AND selection.
 *
 * The panels are unmounted when inactive, so a hidden panel's content is not
 * reachable by screen readers and cannot be tabbed into.
 */

import { useId, useRef, useState, type KeyboardEvent, type ReactNode } from 'react'

export interface TabItem {
  /** Stable key. Used for the React key and the generated element ids. */
  id: string
  /** The visible label. */
  label: string
  /** Optional count badge, e.g. the number of items in the panel. */
  count?: number
}

export interface TabsProps {
  items: TabItem[]
  /** Rendered per tab; receives the item id so the caller can switch. */
  children: (activeId: string) => ReactNode
  /** Which tab to start on. Defaults to the first. */
  defaultActiveId?: string
  /** Accessible name for the tab list. */
  label: string
  className?: string
}

export function Tabs({ items, children, defaultActiveId, label, className }: TabsProps) {
  const baseId = useId()
  const [activeId, setActiveId] = useState(defaultActiveId ?? items[0]?.id ?? '')
  const tabRefs = useRef<Record<string, HTMLButtonElement | null>>({})

  if (items.length === 0) return null

  const activeIndex = Math.max(
    0,
    items.findIndex((i) => i.id === activeId),
  )

  /** Move focus AND selection to the tab at `index` (wrapping). */
  function focusTab(index: number) {
    const next = items[(index + items.length) % items.length]
    if (!next) return
    setActiveId(next.id)
    tabRefs.current[next.id]?.focus()
  }

  function onKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    switch (event.key) {
      case 'ArrowRight':
        event.preventDefault()
        focusTab(activeIndex + 1)
        break
      case 'ArrowLeft':
        event.preventDefault()
        focusTab(activeIndex - 1)
        break
      case 'Home':
        event.preventDefault()
        focusTab(0)
        break
      case 'End':
        event.preventDefault()
        focusTab(items.length - 1)
        break
      default:
        break
    }
  }

  return (
    <div className={className}>
      <div
        role="tablist"
        aria-label={label}
        onKeyDown={onKeyDown}
        className="flex flex-wrap justify-center gap-2 mb-8"
      >
        {items.map((item) => {
          const active = item.id === activeId
          return (
            <button
              key={item.id}
              ref={(el) => {
                tabRefs.current[item.id] = el
              }}
              type="button"
              role="tab"
              id={`${baseId}-tab-${item.id}`}
              aria-selected={active}
              aria-controls={`${baseId}-panel-${item.id}`}
              tabIndex={active ? 0 : -1}
              onClick={() => setActiveId(item.id)}
              className={`inline-flex items-center gap-2 rounded-yada-full border px-5 py-2.5 text-sm font-semibold transition-colors ${
                active
                  ? 'border-primary bg-primary text-surface'
                  : 'border-surface-3 bg-surface text-ink-soft hover:border-primary hover:text-primary'
              }`}
            >
              {item.label}
              {item.count !== undefined && (
                <span
                  className={`rounded-yada-full px-2 py-0.5 text-xs font-bold tabular-nums ${
                    active ? 'bg-surface/25 text-surface' : 'bg-primary/10 text-primary'
                  }`}
                >
                  {item.count}
                </span>
              )}
            </button>
          )
        })}
      </div>

      {items.map((item) => {
        if (item.id !== activeId) return null
        return (
          <div
            key={item.id}
            role="tabpanel"
            id={`${baseId}-panel-${item.id}`}
            aria-labelledby={`${baseId}-tab-${item.id}`}
            tabIndex={0}
            className="focus-visible:outline-2 focus-visible:outline-primary focus-visible:outline-offset-4 rounded-yada-lg"
          >
            {children(item.id)}
          </div>
        )
      })}
    </div>
  )
}

export default Tabs
